import { QuestionCircleOutlined } from "@ant-design/icons";
import { Space, Tooltip } from "antd";
import Breadcrumb, {
  type BreadcrumbItemType,
} from "antd/lib/breadcrumb/Breadcrumb";
import Link from "next/link";
import type { CSSProperties, ReactNode } from "react";
import { useIntl, defineMessages } from "react-intl";

import { formatTenantName } from "../../../lib/formats/tenants";
import { useCurrentUser } from "../../../lib/hooks/basic/users";
import { VideoModal } from "../VideoModal";

export interface ContentHeaderProps {
  style?: CSSProperties;
  title?: ReactNode;
  tooltip?: React.ReactNode;
  breadcrumbs?: BreadcrumbItemType[];
  headerContent?: ReactNode;
  afterTooltip?: ReactNode;
  videoId?: string;
}

const messages = defineMessages({
  home: { id: "homeTenant", defaultMessage: "homeTenant" },
});

function breadcrumbRender(
  route: Partial<BreadcrumbItemType>,
  _params: any,
  items: Partial<BreadcrumbItemType>[]
) {
  const last = items.indexOf(route) === items.length - 1;
  // antd v6 renamed `breadcrumbName` → `title`; accept either for safety.
  const name = route.title ?? route.breadcrumbName;
  return last ? (
    <span style={{ fontWeight: 600 }}>{name}</span>
  ) : (
    <Link href={route.path || ""}>{name}</Link>
  );
}

// Page callers still pass antd v4 shapes: `breadcrumbName` instead of `title`
// and `children` instead of `menu`. antd v6 only converts those for the
// deprecated `routes` prop, so `items` callers have to map them here.
function toItem({ children, ...item }: BreadcrumbItemType): BreadcrumbItemType {
  return {
    ...item,
    title: item.title ?? item.breadcrumbName,
    menu:
      item.menu ??
      (children && {
        items: children.map((child) => ({
          key: child.path,
          label: (
            <Link href={child.path || ""}>
              {child.title ?? child.breadcrumbName}
            </Link>
          ),
        })),
      }),
  };
}

export function ContentHeader({
  tooltip,
  breadcrumbs,
  headerContent,
  afterTooltip,
  videoId,
  style,
}: ContentHeaderProps) {
  const intl = useIntl();
  const { user } = useCurrentUser();
  // otherwise it renders blank white div with paddings
  if (!tooltip && !breadcrumbs && !headerContent) return null;

  return (
    <div className="page-header" style={style}>
      {breadcrumbs && (
        <Space>
          <Breadcrumb
            style={{ fontSize: 16 }}
            itemRender={breadcrumbRender}
            items={[
              {
                path: "/",
                title: intl.formatMessage(messages.home, {
                  tenantName: user && formatTenantName(user.currentTenant),
                }),
              },
              ...breadcrumbs.map(toItem),
            ]}
          />

          {tooltip && (
            <Tooltip title={tooltip}>
              <QuestionCircleOutlined style={{ opacity: 0.5 }} />
            </Tooltip>
          )}

          {videoId && <VideoModal videoId={videoId} />}

          {afterTooltip}
        </Space>
      )}

      {headerContent && <div style={{ marginTop: 8 }}>{headerContent}</div>}
    </div>
  );
}
